import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Combobox, Input } from "../../../components/ui";
import { useCarrierOptions, type CarrierOption } from "../../../features/shipments/carrierTracking";

export interface CarrierTrackingDraft {
  number: string;
  carrierCode: number | null;
}

export const EMPTY_CARRIER_DRAFT: CarrierTrackingDraft = { number: "", carrierCode: null };

/** Misma regla que la API (17TRACK): 5–50 letras, dígitos o guiones. */
export const isValidCarrierNumber = (value: string): boolean =>
  /^[A-Z0-9-]{5,50}$/.test(value.replace(/\s+/g, "").toUpperCase());

interface PickerOption {
  value: number | null;
  label: string;
}

const AUTO_VALUE = null;

/**
 * Guía + paquetería (autocompletado contra /carrierTracking/carriers, con
 * «Detectar automáticamente» por omisión). Lo usan el alta de envío y la
 * tarjeta de Paquetería del detalle.
 */
export default function CarrierTrackingFields({
  value,
  onChange,
  error,
  disabled = false,
}: {
  value: CarrierTrackingDraft;
  onChange: (next: CarrierTrackingDraft) => void;
  error?: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation("envios");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<CarrierOption | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (value.carrierCode === null) setSelected(null);
  }, [value.carrierCode]);

  const carriers = useCarrierOptions(debounced, !disabled);

  const options = useMemo<PickerOption[]>(() => {
    const list = (carriers.data ?? []).map((c) => ({
      value: c.code,
      label: c.country ? `${c.name} · ${c.country}` : c.name,
    }));
    const auto = { value: AUTO_VALUE, label: t("carrier.autoDetect") };
    const keepSelected =
      selected && !list.some((o) => o.value === selected.code)
        ? [{ value: selected.code, label: selected.name }]
        : [];
    return [auto, ...keepSelected, ...list];
  }, [carriers.data, selected, t]);

  const current = options.find((o) => o.value === value.carrierCode) ?? options[0];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Input
        label={t("carrier.number")}
        value={value.number}
        onChange={(e) => onChange({ ...value, number: e.target.value.toUpperCase().slice(0, 60) })}
        placeholder={t("carrier.numberPlaceholder")}
        className="font-mono"
        error={error || undefined}
        disabled={disabled}
        autoComplete="off"
      />
      <div>
        <label className="mb-1.5 block text-sm font-medium text-content">{t("carrier.carrier")}</label>
        <Combobox<PickerOption>
          value={current}
          options={options}
          onChange={(option) => {
            const code = option?.value ?? null;
            const match = (carriers.data ?? []).find((c) => c.code === code) ?? null;
            if (match) setSelected(match);
            onChange({ ...value, carrierCode: code });
          }}
          onInputChange={(input, meta) => {
            if (meta.action === "input-change") setSearch(input);
          }}
          filterOption={null}
          isLoading={carriers.isFetching}
          isDisabled={disabled}
          isClearable={false}
          placeholder={t("carrier.searchCarrier")}
          noOptionsMessage={() => t("carrier.noCarriers")}
          aria-label={t("carrier.carrier")}
        />
      </div>
    </div>
  );
}
